import { Home, Building2, Building } from "lucide-react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export const formatPercent = (value) => {
  return `${value.toFixed(2)}%`;
};

export const PropertyTypeIcon = ({ type }) => {
  switch (type) {
    case 'single_family':
      return <Home className="w-4 h-4" />;
    case 'multi_family':
      return <Building2 className="w-4 h-4" />;
    case 'townhouse':
      return <Building className="w-4 h-4" />;
    default:
      return <Home className="w-4 h-4" />;
  }
};

// Human-readable property type label (used in alt text, breadcrumbs, etc.)
const PROPERTY_TYPE_LABELS = {
  single_family: "Single-family home",
  multi_family: "Multifamily property",
  townhouse: "Townhouse",
  condo: "Condo",
  duplex: "Duplex",
  triplex: "Triplex",
  fourplex: "Fourplex",
};

/**
 * Generate natural, accurate alt text for a property card image.
 * Format: "{Property type} in {City}, {State} — {one key metric}"
 * Example: "Multifamily property in Baltimore, MD — 17% cap rate"
 * Only ONE key metric is included to keep it readable (not keyword-stuffed).
 * Falls back gracefully if data is missing — always returns a usable string.
 *
 * @param {object} property - listing data (address, city, state, property_type, bedrooms)
 * @param {object} [metrics] - optional { cap_rate, cash_flow_monthly } for metric selection
 */
export const buildPropertyAltText = (property, metrics = {}) => {
  if (!property) return "Investment property listing";

  const typeLabel = PROPERTY_TYPE_LABELS[property.property_type] || "Rental property";
  const beds = property.bedrooms ? `${property.bedrooms}-bed ` : "";
  const city = property.city || "";
  const state = property.state || "";
  const location = city && state ? `${city}, ${state}` : (city || state || "the US");

  // Pick ONE metric — prefer cap rate (cleaner for image search), fall back to cash flow.
  let metric = "";
  const cap = metrics.cap_rate;
  const cf = metrics.cash_flow_monthly;
  if (typeof cap === "number" && cap > 0) {
    metric = ` — ${cap.toFixed(1)}% cap rate`;
  } else if (typeof cf === "number" && cf > 0) {
    metric = ` — $${Math.round(cf).toLocaleString()}/mo cash flow`;
  } else if (typeof cf === "number" && cf < 0) {
    // Don't advertise negative cash flow; just describe the property.
    metric = "";
  }

  // Lowercase the type when following an article ("4-bed multifamily property" flows better)
  const typeForBeds = beds ? typeLabel.toLowerCase() : typeLabel;
  return `${beds}${typeForBeds} in ${location}${metric}`;
};
